import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Section, SectionHeader, RowGroup, ToggleRow } from "./_helpers";
import { Key, Mail, ShieldCheck, Monitor, Pause, Download, Trash2, AlertTriangle, Undo2, Copy, Check, Mic, HelpCircle, Gift } from "lucide-react";

function VerifyEmailSection({ user, refreshAuth }) {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [busy, setBusy] = useState(false);

  if (!user || user.email_verified !== false) return null;

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email/send");
      toast.success(t("settings.pages.konto.verify_sent", "Ny verifiserings-e-post sendt. Sjekk innboksen din."));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section testId="verify-email-section">
      <SectionHeader
        icon={Mail}
        title={k("verify_email_title")}
        desc={k("verify_email_desc").replace("{{email}}", user.email)}
      />
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <button onClick={send} disabled={busy} className="btn-magenta" data-testid="verify-email-resend-btn">
          {busy ? k("sending") : k("send_again")}
        </button>
        <button onClick={refreshAuth} className="btn-ghost" data-testid="verify-email-recheck-btn">
          {k("i_verified")}
        </button>
      </div>
    </Section>
  );
}

function ChangePasswordSection() {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next !== confirm) return toast.error(t("auth.passwords_no_match", "Passordene matcher ikke"));
    if (next.length < 8) return toast.error(t("auth.password_too_short", "Nytt passord må være minst 8 tegn"));
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("auth.password_changed", "Passordet er endret"));
      setCur(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section testId="change-password-section">
      <SectionHeader icon={Key} title={k("change_password")} desc={k("change_password_desc")} />
      <div className="space-y-3 max-w-md">
        <input type="password" placeholder={k("current_password")} value={cur} onChange={(e)=>setCur(e.target.value)}
          className="input-editorial w-full" data-testid="pwd-current" autoComplete="current-password" />
        <input type="password" placeholder={k("new_password")} value={next} onChange={(e)=>setNext(e.target.value)}
          className="input-editorial w-full" data-testid="pwd-new" autoComplete="new-password" />
        <input type="password" placeholder={k("confirm_new_password")} value={confirm} onChange={(e)=>setConfirm(e.target.value)}
          className="input-editorial w-full" data-testid="pwd-confirm" autoComplete="new-password" />
        <button onClick={submit} disabled={busy || !cur || !next} className="btn-magenta" data-testid="pwd-submit">
          {busy ? k("saving") : k("change_password")}
        </button>
      </div>
    </Section>
  );
}

function ChangeEmailSection({ user }) {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [step, setStep] = useState("request");
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mockCode, setMockCode] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    if (!newEmail || !password) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/change-email/request", { new_email: newEmail, password });
      toast.success("Kode generert");
      setMockCode(r.data.mock_code || "");
      setStep("confirm");
      setPassword("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-email/confirm", { code });
      toast.success(t("settings.pages.konto.email_updated", "E-post oppdatert"));
      setStep("request"); setNewEmail(""); setCode(""); setMockCode("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section testId="change-email-section">
      <SectionHeader icon={Mail} title={k("change_email")} desc={k("change_email_desc").replace("{{email}}", user?.email || "—")} />
      {step === "request" ? (
        <div className="space-y-3 max-w-md">
          <input type="email" placeholder={k("new_email")} value={newEmail} onChange={(e)=>setNewEmail(e.target.value)}
            className="input-editorial w-full" data-testid="email-new" />
          <input type="password" placeholder={k("password_to_confirm")} value={password} onChange={(e)=>setPassword(e.target.value)}
            className="input-editorial w-full" data-testid="email-password" />
          <button onClick={request} disabled={busy || !newEmail || !password} className="btn-magenta" data-testid="email-request">
            {busy ? k("sending") : k("send_code")}
          </button>
        </div>
      ) : (
        <div className="space-y-3 max-w-md">
          {mockCode && (
            <div className="border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-4">
              <div className="font-meta text-[var(--gold)] mb-1">MOCK</div>
              <div className="font-mono text-2xl tracking-widest text-[var(--cream)]">{mockCode}</div>
            </div>
          )}
          <input type="text" placeholder={k("code_6")} value={code} onChange={(e)=>setCode(e.target.value)}
            className="input-editorial w-full" data-testid="email-code" maxLength={6} />
          <div className="flex gap-3">
            <button onClick={confirm} disabled={busy || code.length !== 6} className="btn-magenta" data-testid="email-confirm">
              {busy ? k("verifying") : k("confirm")}
            </button>
            <button onClick={()=>{setStep("request"); setCode(""); setMockCode("");}} className="btn-ghost">{k("cancel")}</button>
          </div>
        </div>
      )}
    </Section>
  );
}

function TwoFactorSection() {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/auth/2fa/status").then(r => setStatus(r.data)).catch(()=>{}); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetupData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/verify", { code });
      setBackupCodes(r.data.backup_codes);
      setStatus({ enabled: true, activated_at: new Date().toISOString(), backup_codes_remaining: 10 });
      setSetupData(null);
      setCode("");
      toast.success("2FA aktivert");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Er du sikker på at du vil skru av 2FA?")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code, password });
      setStatus({ enabled: false, activated_at: null, backup_codes_remaining: 0 });
      setCode(""); setPassword("");
      toast.success("2FA er skrudd av");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    toast.success("Koder kopiert");
  };

  return (
    <Section testId="2fa-section">
      <SectionHeader icon={ShieldCheck} title={k("two_factor")}
        desc={k("two_factor_desc")} />

      {!status ? <div className="text-[var(--cream-soft)]">{t("common.loading")}</div> :
       backupCodes.length > 0 ? (
        <div className="border border-[var(--magenta)]/30 bg-[var(--magenta)]/5 p-5">
          <div className="font-fraunces italic text-xl text-[var(--cream)] mb-3">{k("backup_codes")}</div>
          <p className="font-manrope text-sm text-[var(--cream-soft)] mb-4">
            {k("backup_codes_desc")}
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm text-[var(--cream)] mb-4">
            {backupCodes.map((c, i) => <div key={i} className="border border-white/10 p-2">{c}</div>)}
          </div>
          <div className="flex gap-3">
            <button onClick={copyBackup} className="btn-ghost inline-flex items-center gap-2" data-testid="2fa-copy-backup">
              <Copy className="h-3.5 w-3.5" /> {k("copy_all")}
            </button>
            <button onClick={()=>setBackupCodes([])} className="btn-magenta" data-testid="2fa-done">{k("i_saved_them")}</button>
          </div>
        </div>
      ) : status.enabled ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[var(--cream)]">
            <Check className="h-4 w-4 text-emerald-400" /> {k("twofa_active").replace("{{count}}", status.backup_codes_remaining)}
          </div>
          <div className="space-y-3 max-w-md">
            <input type="password" placeholder={k("current_password")} value={password} onChange={(e)=>setPassword(e.target.value)}
              className="input-editorial w-full" data-testid="2fa-disable-password" />
            <input type="text" placeholder={k("code_6")} value={code} onChange={(e)=>setCode(e.target.value)}
              className="input-editorial w-full" maxLength={6} data-testid="2fa-disable-code" />
            <button onClick={disable} disabled={busy || !code || !password} className="btn-ghost" data-testid="2fa-disable-btn">
              {k("twofa_disable")}
            </button>
          </div>
        </div>
      ) : setupData ? (
        <div className="space-y-4 max-w-md">
          <p className="font-manrope text-sm text-[var(--cream-soft)] whitespace-pre-line">
            {k("scan_qr")}
          </p>
          <div className="bg-white p-4 inline-block">
            <img src={setupData.qr_code_data_url} alt="QR" className="w-48 h-48" />
          </div>
          <details className="font-manrope text-xs text-[var(--cream-soft)]">
            <summary className="cursor-pointer">{k("cant_scan")}</summary>
            <div className="font-mono text-sm text-[var(--cream)] mt-2 break-all">{setupData.secret}</div>
          </details>
          <input type="text" placeholder={k("code_6")} value={code} onChange={(e)=>setCode(e.target.value)}
            className="input-editorial w-full" maxLength={6} data-testid="2fa-verify-code" />
          <div className="flex gap-3">
            <button onClick={verify} disabled={busy || code.length !== 6} className="btn-magenta" data-testid="2fa-verify-btn">
              {k("verify_activate")}
            </button>
            <button onClick={()=>{setSetupData(null); setCode("");}} className="btn-ghost">{k("cancel")}</button>
          </div>
        </div>
      ) : (
        <button onClick={startSetup} disabled={busy} className="btn-magenta" data-testid="2fa-start-btn">
          {busy ? k("generating") : k("generate_2fa")}
        </button>
      )}
    </Section>
  );
}

function ActiveSessionsSection() {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users/me/sessions").then(r=>setSessions(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const revokeOne = async (sid) => {
    if (!window.confirm("Logg ut denne økten?")) return;
    try {
      await api.post(`/users/me/sessions/${sid}/revoke`);
      toast.success("Økt logget ut");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revokeOthers = async () => {
    if (!window.confirm("Logg ut fra alle andre enheter?")) return;
    setBusy(true);
    try {
      const r = await api.post("/users/me/sessions/revoke-others");
      toast.success(`${r.data.revoked_count} andre økter logget ut`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section testId="sessions-section">
      <SectionHeader icon={Monitor} title={k("active_sessions")}
        desc={k("active_sessions_desc")} />
      {sessions.length === 0 ? (
        <p className="font-fraunces italic text-[var(--cream-soft)]">{k("no_sessions")}</p>
      ) : (
        <ul className="border-y border-white/10 divide-y divide-white/5 mb-4" data-testid="sessions-list">
          {sessions.map((s) => (
            <li key={s.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-manrope text-sm text-[var(--cream)] flex items-center gap-2">
                  <span className="truncate">{s.device_label || k("unknown_device")}</span>
                  {s.is_current && <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--magenta)] shrink-0">{k("this_device")}</span>}
                </div>
                <div className="font-meta mt-1 text-[10px] text-[var(--cream-faint)]">
                  {k("last_active")} {s.last_seen_at ? new Date(s.last_seen_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}
                  {s.location_label && <> · {s.location_label}</>}
                  {s.ip && <> · {s.ip}</>}
                </div>
              </div>
              {!s.is_current && (
                <button onClick={() => revokeOne(s.id)}
                  className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)] shrink-0"
                  data-testid={`revoke-session-${s.id}`}>
                  {k("log_out")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <button onClick={revokeOthers} disabled={busy} className="btn-ghost" data-testid="revoke-others-sessions">
        {k("log_out_all_others")}
      </button>
    </Section>
  );
}

function AccountControlSection() {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const { user: me } = useAuth();
  const [status, setStatus] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get("/me/deletion-status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  const exportData = async () => {
    setBusy(true);
    try {
      const r = await api.get("/me/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `norsk-erotikk-eksport-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dine data er lastet ned");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const pauseAccount = async () => {
    if (!window.confirm("Pause kontoen din? Du logges automatisk inn igjen ved neste innlogging.")) return;
    setBusy(true);
    try {
      await api.post("/users/me/pause");
      toast.success("Kontoen er på pause");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const requestDelete = async () => {
    if (!password) return toast.error("Skriv inn passordet ditt");
    setBusy(true);
    try {
      const r = await api.post("/me/delete-account", { password, reason });
      setStatus({ scheduled_deletion_at: r.data.scheduled_deletion_at });
      setShowDelete(false);
      setPassword(""); setReason("");
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancelDelete = async () => {
    if (!window.confirm("Vil du angre slettings-forespørselen?")) return;
    setBusy(true);
    try {
      await api.post("/me/cancel-deletion");
      setStatus({ scheduled_deletion_at: null });
      toast.success("Slettings-forespørselen er kansellert.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const deletionDate = status?.scheduled_deletion_at
    ? new Date(status.scheduled_deletion_at).toLocaleDateString("nb-NO", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <Section testId="account-control-section">
      <div className="border-t border-[var(--magenta)]/30 pt-10">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="h-4 w-4 text-[var(--magenta)]" />
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">{k("account_actions")}</h2>
        </div>
        <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-6">
          {k("account_actions_desc")}
        </p>

        <div className="border border-white/10 p-5 mb-4 flex items-start gap-4">
          <HelpCircle className="h-5 w-5 text-[var(--cream-soft)] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-manrope text-base text-[var(--cream)] mb-1">{k("restart_tour")}</h3>
            <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3">
              {k("restart_tour_desc")}
            </p>
            <button onClick={async () => {
              try {
                await api.post("/me/onboarding-reset");
                toast.success(t("settings.pages.konto.tour_will_start", "Omvisning starter ved neste sidelasting!"));
                setTimeout(() => window.location.assign("/"), 800);
              } catch { toast.error(t("settings.pages.konto.tour_reset_failed", "Kunne ikke nullstille omvisningen.")); }
            }} className="btn-ghost" data-testid="restart-tour-btn">
              {k("restart_tour_btn")}
            </button>
          </div>
        </div>

        {me?.is_creator && (
          <div className="border border-[var(--magenta)]/30 p-5 mb-4 flex items-start gap-4">
            <HelpCircle className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-manrope text-base text-[var(--cream)] mb-1">{k("restart_creator_tour")}</h3>
              <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3">
                {k("restart_creator_tour_desc")}
              </p>
              <button onClick={async () => {
                try {
                  await api.post("/me/creator-tour-reset");
                  toast.success(t("settings.pages.konto.creator_tour_will_start", "Skaper-omvisning starter neste gang du besøker skapersiden din!"));
                  setTimeout(() => {
                    const target = me?.creator_handle
                      ? me.creator_handle.toLowerCase().replace(/\s+/g, "-")
                      : me?.username;
                    window.location.assign(`/skaper/${target}`);
                  }, 800);
                } catch { toast.error(t("settings.pages.konto.creator_tour_reset_failed", "Kunne ikke nullstille skaper-omvisningen.")); }
              }} className="btn-ghost" data-testid="restart-creator-tour-btn">
                {k("restart_creator_tour_btn")}
              </button>
            </div>
          </div>
        )}

        <div className="border border-white/10 p-5 mb-4 flex items-start gap-4">
          <Pause className="h-5 w-5 text-[var(--cream-soft)] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-manrope text-base text-[var(--cream)] mb-1">{k("pause_account")}</h3>
            <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3">
              {k("pause_account_desc")}
            </p>
            <button onClick={pauseAccount} disabled={busy} className="btn-ghost" data-testid="pause-account-btn">
              {k("pause_account_btn")}
            </button>
          </div>
        </div>

        <div className="border border-white/10 p-5 mb-4 flex items-start gap-4">
          <Download className="h-5 w-5 text-[var(--cream-soft)] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-manrope text-base text-[var(--cream)] mb-1">{k("download_data")}</h3>
            <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed mb-3">
              {k("download_data_desc")}
            </p>
            <button onClick={exportData} disabled={busy} className="btn-ghost inline-flex items-center gap-2" data-testid="export-data-btn">
              <Download className="h-3.5 w-3.5" /> {busy ? k("exporting") : k("download_json")}
            </button>
          </div>
        </div>

        <div className="border border-[var(--magenta)]/40 p-5">
          <div className="flex items-start gap-4">
            <Trash2 className="h-5 w-5 text-[var(--magenta)] shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-manrope text-base text-[var(--cream)] mb-1">{k("delete_account")}</h3>
              {status?.scheduled_deletion_at ? (
                <>
                  <p
                    className="font-manrope text-sm text-[var(--cream)] mb-3"
                    dangerouslySetInnerHTML={{ __html: k("deleted_at").replace("{{date}}", `<span class="text-[var(--magenta)]">${deletionDate}</span>`) }}
                  />
                  <button onClick={cancelDelete} disabled={busy} className="btn-ghost inline-flex items-center gap-2" data-testid="cancel-deletion-btn">
                    <Undo2 className="h-3.5 w-3.5" /> {k("undo_delete")}
                  </button>
                </>
              ) : !showDelete ? (
                <>
                  <p className="font-manrope text-sm text-[var(--cream-soft)] mb-3">
                    {k("delete_intro")}
                  </p>
                  <button onClick={() => setShowDelete(true)} className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--magenta)] hover:text-[var(--cream)] inline-flex items-center gap-2" data-testid="open-delete-flow-btn">
                    <Trash2 className="h-3.5 w-3.5" /> {k("delete_btn")}
                  </button>
                </>
              ) : (
                <div className="space-y-3" data-testid="delete-flow">
                  <p className="font-manrope text-sm text-[var(--cream-soft)]">
                    {k("delete_recommend")}
                  </p>
                  <input type="password" placeholder={k("current_password")} value={password} onChange={(e)=>setPassword(e.target.value)}
                    className="input-editorial w-full max-w-md" data-testid="delete-password-input" />
                  <textarea placeholder={k("delete_reason_placeholder")} value={reason} onChange={(e)=>setReason(e.target.value)}
                    rows={2} maxLength={500} className="input-editorial w-full max-w-md" data-testid="delete-reason-input" />
                  <div className="flex gap-3">
                    <button onClick={requestDelete} disabled={busy || !password} className="btn-magenta" data-testid="confirm-delete-btn">
                      {busy ? k("sending") : k("confirm_deletion")}
                    </button>
                    <button onClick={() => { setShowDelete(false); setPassword(""); setReason(""); }} className="btn-ghost">{k("cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function CreatorHandleSection({ user }) {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [handle, setHandle] = useState(user?.creator_handle || "");
  const [busy, setBusy] = useState(false);
  if (!user?.is_creator) return null;

  const save = async () => {
    if (handle.length < 3) return toast.error(t("settings.pages.konto.handle_min_3", "Artistnavn må være minst 3 tegn"));
    if (!/^[a-zA-Z0-9_]+$/.test(handle)) return toast.error(t("settings.pages.konto.handle_invalid_chars", "Kun bokstaver, tall og _ tillatt"));
    setBusy(true);
    try {
      await api.put("/users/me/creator-handle", { creator_handle: handle });
      toast.success(t("settings.pages.konto.handle_saved", "Artistnavn satt til @{{handle}}", { handle }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("settings.pages.konto.handle_remove_confirm", "Fjerne artistnavnet? Creator-siden vil falle tilbake til ditt private brukernavn."))) return;
    setBusy(true);
    try {
      await api.delete("/users/me/creator-handle");
      toast.success(t("settings.pages.konto.handle_removed", "Artistnavn fjernet"));
      setHandle("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Section testId="creator-handle-section">
      <SectionHeader icon={Mic} title={k("creator_handle")}
        desc={k("creator_handle_desc")} />
      <div className="flex gap-2 items-center max-w-md">
        <span className="font-meta text-[var(--cream-faint)]">@</span>
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="RoseNoire"
          maxLength={24}
          className="input-editorial flex-1"
          data-testid="creator-handle-input"
        />
        <button onClick={save} disabled={busy || !handle} className="btn-magenta text-xs px-4" data-testid="creator-handle-save">
          {busy ? k("saving") : k("creator_handle_save")}
        </button>
      </div>
      {user.creator_handle && (
        <div className="mt-3 flex items-center gap-3">
          <span className="font-meta text-[10px] text-[var(--cream-soft)]">
            {k("creator_page")}: <Link to={`/skaper/${user.creator_handle.toLowerCase().replace(/\s+/g, "-")}`} className="text-[var(--magenta)] hover:underline">/skaper/{user.creator_handle}</Link>
          </span>
          <button onClick={remove} disabled={busy} className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="creator-handle-remove">
            {k("creator_handle_remove")}
          </button>
        </div>
      )}
    </Section>
  );
}

function BankIdVerifySection() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/bankid/status");
        setStatus(r.data);
      } catch { setStatus({ glimt_eligible: false, bankid_verified: false }); }
    })();
  }, []);
  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/me/bankid/start", { next_url: "/innstillinger/konto" });
      if (r.data.already_verified) {
        setStatus((s) => ({ ...(s || {}), bankid_verified: true, glimt_eligible: true }));
        toast.success("Allerede BankID-verifisert");
      } else if (r.data.redirect_url) {
        window.location.href = r.data.redirect_url;
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };
  if (!status) return null;
  const verified = status.bankid_verified;
  return (
    <Section testId="bankid-verify-section">
      <SectionHeader
        icon={ShieldCheck}
        title="BankID-verifisering"
        desc="Bekreft identitet med BankID (via Strex Id). Kreves for Glimt hvis du ikke har kjøpt tokens."
      />
      {verified ? (
        <div className="flex items-center gap-2 text-emerald-400" data-testid="bankid-verified-status">
          <ShieldCheck className="h-5 w-5" />
          <span>Verifisert{status.bankid_verified_at ? ` — ${new Date(status.bankid_verified_at).toLocaleDateString("no-NO")}` : ""}</span>
        </div>
      ) : (
        <div className="space-y-3">
          {status.verification_level >= 1 && (
            <p className="text-xs text-[var(--cream-soft)]" data-testid="bankid-payment-verified-hint">
              Du har allerede kjøpt tokens og teller som alders-verifisert. BankID gir sterkere identitets­bevis.
            </p>
          )}
          <button
            onClick={start}
            disabled={starting}
            className="btn-magenta inline-flex items-center gap-2"
            data-testid="bankid-start-verify-btn"
          >
            <ShieldCheck className="h-4 w-4" />
            {starting ? "Starter BankID…" : "Verifiser med BankID"}
          </button>
        </div>
      )}
    </Section>
  );
}



export default function KontoPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  return (
    <>
      <VerifyEmailSection user={user} refreshAuth={refresh} />
      <ChangePasswordSection />
      <ChangeEmailSection user={user} />
      <CreatorHandleSection user={user} />
      <TwoFactorSection />
      <ActiveSessionsSection />
      <AccountControlSection />
      <BankIdVerifySection />
      <Section testId="seller-verify-link-section">
        <SectionHeader icon={ShieldCheck} title={t("settings.pages.konto.seller_verify_link_title")}
          desc={t("settings.pages.konto.seller_verify_link_desc")} />
        <Link to="/innstillinger/selger-verifisering" className="btn-ghost inline-flex items-center gap-2" data-testid="seller-verify-link">
          {t("settings.pages.konto.go_to_verification")}
        </Link>
      </Section>

      <PromoCodeSection />
    </>
  );
}

// ============================================================
//  PromoCodeSection – løse inn kode for gratis VIP / Creator-VIP
// ============================================================
function PromoCodeSection() {
  const { t } = useTranslation();
  const k = (key) => t(`settings.pages.konto.${key}`);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const clean = code.trim().toUpperCase().replace(/\s+/g, "");
    if (clean.length < 4) {
      toast.error(t("settings.pages.konto.promo_invalid", "Skriv inn en gyldig kode."));
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      try {
        const r = await api.post("/me/promo-codes/redeem", { code: clean });
        setResult({ ...r.data, source: "promo" });
        const kindLabel = r.data.kind === "creator_vip" ? "Creator-VIP" : "VIP";
        toast.success(t("settings.pages.konto.promo_activated", "{{kind}} aktivert i {{days}} dager!", { kind: kindLabel, days: r.data.days_added }));
        setCode("");
        return;
      } catch (promoErr) {
        if (promoErr.response?.status !== 404) throw promoErr;
      }
      const r2 = await api.post("/me/campaign-codes/redeem-free-vip", { code: clean });
      setResult({
        kind: "vip",
        days_added: r2.data.vip_days_granted,
        source: "campaign",
      });
      toast.success(t("settings.pages.konto.promo_activated", "{{kind}} aktivert i {{days}} dager!", { kind: "VIP", days: r2.data.vip_days_granted }));
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section testId="promo-code-section">
      <SectionHeader
        icon={Gift}
        title={k("promo_code")}
        desc={k("promo_code_desc")}
      />
      <div className="flex gap-2 max-w-md">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD-1234-XY"
          className="input-editorial flex-1 uppercase tracking-[0.15em]"
          maxLength={20}
          data-testid="promo-code-input"
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        />
        <button
          onClick={submit}
          disabled={busy || code.trim().length < 4}
          className="btn-magenta px-5 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="promo-code-submit"
        >
          {busy ? k("redeeming") : k("redeem")}
        </button>
      </div>
      {result && (
        <div className="mt-3 text-sm text-[var(--cream-soft)]">
          {k("promo_extended").replace("{{kind}}", result.kind === "creator_vip" ? "Creator-VIP" : "VIP").replace("{{days}}", result.days_added)}
        </div>
      )}
    </Section>
  );
}
